import { z } from 'zod';

import type { ValidationIssue } from '../core/issues.js';
import type { ActivityCheckContext, ActivityDefinition } from './types.js';

import { trimmed } from './_checks.js';
import { draftFields } from './_shared.js';
import { isEmbedSrc } from './embedSrc.js';
import { activityIssue, asList, asRecord } from './types.js';

const issue = activityIssue('html_embed');

const PARAM_KEY = /^[a-z]\w*$/i;
const OUTCOME_ID = /^[a-z][a-z0-9_-]*$/;

/** The longest outcome a file may report; the player's message guard refuses anything longer. */
export const HTML_EMBED_OUTCOME_ID_MAX = 64;

/** Any outcome the node does not declare finishes the node here, so it always needs a route. */
export const HTML_EMBED_DEFAULT_OUTCOME = 'complete';

/** Long enough for a large game on a slow connection, short enough that a stuck student gets the card. */
export const HTML_EMBED_TIMEOUT_SECONDS = { min: 1, max: 120 } as const;

/** What an author is told a file path has to look like, written once. */
export const EMBED_SRC_FORM = 'use the entry file of a folder under /embeds/, such as /embeds/my-game/index.html';

/** The player loads `src` exactly as stored, so a path padded with spaces is checked as written. */
const srcIssues = (setup: Record<string, unknown>, ctx: ActivityCheckContext): ValidationIssue[] => {
  const path = [...ctx.path, 'setup', 'src'];
  if (!trimmed(setup.src)) {
    return [issue('noSrc', 'No file path is written, so there is nothing to play.', path, ctx)];
  }
  if (!isEmbedSrc(setup.src)) {
    return [issue('badSrc', `‘${String(setup.src)}’ is not a file the player will load: ${EMBED_SRC_FORM}.`, path, ctx, setup.src)];
  }
  return [];
};

/** The title names the frame for screen readers and heads the card shown when the file fails to load. */
const titleIssues = (setup: Record<string, unknown>, ctx: ActivityCheckContext): ValidationIssue[] =>
  trimmed(setup.title)
    ? []
    : [issue('noTitle', 'No title is written. Screen readers and the did-not-load card have nothing to call the activity.', [...ctx.path, 'setup', 'title'], ctx)];

const paramIssues = (setup: Record<string, unknown>, ctx: ActivityCheckContext): ValidationIssue[] =>
  Object.entries(asRecord(setup.params)).flatMap(([key, value]) => {
    const path = [...ctx.path, 'setup', 'params', key];
    if (!PARAM_KEY.test(key)) {
      return [issue('badParam', `Param name ‘${key}’ must start with a letter and use only letters, digits and underscores.`, path, ctx, key)];
    }
    if (typeof value !== 'string') {
      return [issue('badParam', `Param ‘${key}’ must be text. The file is only sent text values.`, path, ctx, value)];
    }
    return [];
  });

const outcomeIssues = (setup: Record<string, unknown>, ctx: ActivityCheckContext): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const seen = new Set<string>();

  for (const [index, outcome] of asList(setup.outcomes).entries()) {
    const path = [...ctx.path, 'setup', 'outcomes', index];
    if (typeof outcome !== 'string' || !OUTCOME_ID.test(outcome)) {
      issues.push(issue('badOutcome', `Outcome ${index + 1} must start with a lowercase letter and use only lowercase letters, digits, underscores and hyphens.`, path, ctx, outcome));
      continue;
    }
    if (outcome.length > HTML_EMBED_OUTCOME_ID_MAX) {
      issues.push(issue('badOutcome', `Outcome ${index + 1} is longer than ${HTML_EMBED_OUTCOME_ID_MAX} characters, and the player ignores a longer one.`, path, ctx, outcome));
      continue;
    }
    if (seen.has(outcome)) {
      issues.push(issue('badOutcome', `Outcome ‘${outcome}’ is listed more than once.`, path, ctx, outcome));
      continue;
    }
    seen.add(outcome);
    if (ctx.outcomeIds && !ctx.outcomeIds.includes(outcome)) {
      issues.push(issue(
        'outcomeWithoutRoute',
        `Outcome ‘${outcome}’ is not one of the node’s outcomes (${ctx.outcomeIds.join(', ') || 'none'}), so a file reporting it routes nowhere.`,
        path,
        ctx,
        outcome,
      ));
    }
  }

  if (ctx.outcomeIds && !ctx.outcomeIds.includes(HTML_EMBED_DEFAULT_OUTCOME)) {
    issues.push(issue(
      'noCompleteRoute',
      `The node has no ‘${HTML_EMBED_DEFAULT_OUTCOME}’ outcome. Any outcome the file reports that is not listed finishes there, so the node needs a route for it.`,
      [...ctx.path, 'setup', 'outcomes'],
      ctx,
      ctx.outcomeIds,
    ));
  }

  return issues;
};

/**
 * `params` and `outcomes` are read loosely by the shape so that a wrong entry is reported by
 * `check` under this type's own codes, where the editor can mark the row it is about.
 */
export const htmlEmbed: ActivityDefinition = {
  type: 'html_embed',
  summary: 'The entry HTML file of a folder under /embeds/, played in a sandboxed frame that fills the node, finishing with the outcome the file reports.',
  setup: z.looseObject({
    ...draftFields,
    src: z.string().optional(),
    title: z.string().optional(),
    params: z.record(z.string(), z.unknown()).optional(),
    outcomes: z.array(z.unknown()).optional(),
    aspect: z.enum(['fill', '16:9', '4:3', '9:16']).optional(),
    orientationHint: z.enum(['any', 'landscape']).optional(),
    timeoutSeconds: z.number().int().min(HTML_EMBED_TIMEOUT_SECONDS.min).max(HTML_EMBED_TIMEOUT_SECONDS.max).optional(),
  }),
  check: (setup, ctx) => [
    ...srcIssues(setup, ctx),
    ...titleIssues(setup, ctx),
    ...paramIssues(setup, ctx),
    ...outcomeIssues(setup, ctx),
  ],
};
